import React, { useEffect, useState } from "react";
import { Button, Modal, Tag, message, Image, Empty, Form, Input, Upload, DatePicker, Select, InputNumber, Dropdown } from "antd";
import {
  TrophyOutlined, EyeOutlined, PlusOutlined, UploadOutlined, CalendarOutlined,
  EditOutlined, DeleteOutlined, MoreOutlined, PoweroffOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import {
  getContestsApi,
  createContestApi,
  getContestLeaderboardApi,
  endContestApi,
  createContestEntryApi,
  updateContestEntryApi,
  setContestEntryActiveApi,
  deleteContestEntryApi,
  uploadImageApi,
  updateContestDatesApi,
  setContestActiveApi,
  deleteContestApi,
} from "../../services/api";
import AppTable from "../../common/AppTable";

const MONTH_NAMES = [
  "", "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const ACCENT = "#534ab7";

export default function Contests() {
  const [contests, setContests] = useState([]);
  const [leaderboardOpen, setLeaderboardOpen] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [activeContest, setActiveContest] = useState(null);
  const [loadingBoard, setLoadingBoard] = useState(false);

  const [addOpen, setAddOpen]   = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving]     = useState(false);
  const [form] = Form.useForm();

  const [datesOpen, setDatesOpen]   = useState(false);
  const [editingContest, setEditingContest] = useState(null);
  const [savingDates, setSavingDates] = useState(false);
  const [datesForm] = Form.useForm();
  const [editBannerUrl, setEditBannerUrl] = useState("");
  const [editBannerUploading, setEditBannerUploading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [creatingContest, setCreatingContest] = useState(false);
  const [createForm] = Form.useForm();
  const [createPhotoUrl, setCreatePhotoUrl] = useState("");
  const [createUploading, setCreateUploading] = useState(false);

  const fetchContests = async () => {
    try {
      const res = await getContestsApi();
      setContests(res.data.data || []);
    } catch {
      message.error("Failed to load contests");
    }
  };

  useEffect(() => { fetchContests(); }, []);

  const openCreateContest = () => {
    setCreateOpen(true);
    setCreatePhotoUrl("");
    createForm.resetFields();
    createForm.setFieldsValue({ year: dayjs().year() });
  };

  const handleCreateUpload = async (file) => {
    setCreateUploading(true);
    try {
      const res = await uploadImageApi(file, "contests");
      setCreatePhotoUrl(res?.data?.data || "");
    } catch {
      message.error("Image upload failed");
    } finally {
      setCreateUploading(false);
    }
    return false;
  };

  const handleCreateContest = async () => {
    try {
      const values = await createForm.validateFields();
      if (!values.endTime.isAfter(values.startTime)) {
        message.error("End date must be after start date");
        return;
      }
      setCreatingContest(true);
      await createContestApi({
        name: values.name,
        month: values.month,
        year: values.year,
        startTime: values.startTime.format("YYYY-MM-DDTHH:mm:ss"),
        endTime: values.endTime.format("YYYY-MM-DDTHH:mm:ss"),
        photoUrl: createPhotoUrl || null,
      });
      message.success("Contest created");
      setCreateOpen(false);
      fetchContests();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.data || "Failed to create contest — one may already exist for that month");
    } finally {
      setCreatingContest(false);
    }
  };

  const handleToggleContestActive = async (contest) => {
    try {
      await setContestActiveApi(contest.id, contest.status !== "ACTIVE");
      message.success(contest.status === "ACTIVE" ? "Contest deactivated" : "Contest activated");
      fetchContests();
    } catch {
      message.error("Failed to update contest status");
    }
  };

  const handleDeleteContest = (contest) => {
    Modal.confirm({
      title: `Delete ${MONTH_NAMES[contest.month]} ${contest.year}?`,
      content: "This permanently removes the contest and all of its entries, likes and comments. This cannot be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteContestApi(contest.id);
          message.success("Contest deleted");
          fetchContests();
        } catch {
          message.error("Failed to delete contest");
        }
      },
    });
  };

  const openLeaderboard = async (contest) => {
    setActiveContest(contest);
    setLeaderboardOpen(true);
    setLoadingBoard(true);
    try {
      const res = await getContestLeaderboardApi(contest.id);
      setLeaderboard(res.data.data || []);
    } catch {
      message.error("Failed to load leaderboard");
      setLeaderboard([]);
    } finally {
      setLoadingBoard(false);
    }
  };

  const openAddEntry = () => {
    setEditingEntry(null);
    setAddOpen(true);
    setPhotoUrl("");
    form.resetFields();
  };

  const openEditEntry = (entry) => {
    setEditingEntry(entry);
    setAddOpen(true);
    setPhotoUrl(entry.photoUrl || "");
    form.setFieldsValue({ caption: entry.caption });
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const res = await uploadImageApi(file, "contests");
      setPhotoUrl(res?.data?.data || "");
    } catch {
      message.error("Image upload failed");
    } finally {
      setUploading(false);
    }
    return false;
  };

  const handleSaveEntry = async () => {
    try {
      const values = await form.validateFields();
      if (!photoUrl) { message.error("Please upload a photo"); return; }
      setSaving(true);
      if (editingEntry) {
        await updateContestEntryApi(editingEntry.id, { caption: values.caption, photoUrl });
        message.success("Entry updated");
      } else {
        await createContestEntryApi({ caption: values.caption, photoUrl });
        message.success("Entry added");
      }
      setAddOpen(false);
      fetchContests();
      if (activeContest) openLeaderboard(activeContest);
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.data || "Failed to save entry");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEntryActive = async (entry) => {
    try {
      await setContestEntryActiveApi(entry.id, !entry.active);
      message.success(entry.active ? "Entry deactivated" : "Entry reactivated");
      if (activeContest) openLeaderboard(activeContest);
    } catch {
      message.error("Failed to update entry");
    }
  };

  const handleDeleteEntry = (entry) => {
    Modal.confirm({
      title: "Delete this entry?",
      content: "This permanently removes the photo, caption, likes and comments. This cannot be undone.",
      okText: "Delete",
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await deleteContestEntryApi(entry.id);
          message.success("Entry deleted");
          if (activeContest) openLeaderboard(activeContest);
        } catch {
          message.error("Failed to delete entry");
        }
      },
    });
  };

  const openEditDates = (contest) => {
    setEditingContest(contest);
    setEditBannerUrl(contest.bannerUrl || "");
    datesForm.setFieldsValue({
      name:      contest.name || "",
      startTime: contest.startTime ? dayjs(contest.startTime) : null,
      endTime:   contest.endTime   ? dayjs(contest.endTime)   : null,
    });
    setDatesOpen(true);
  };

  const handleEditBannerUpload = async (file) => {
    setEditBannerUploading(true);
    try {
      const res = await uploadImageApi(file, "contests");
      setEditBannerUrl(res?.data?.data || "");
    } catch {
      message.error("Image upload failed");
    } finally {
      setEditBannerUploading(false);
    }
    return false;
  };

  const handleSaveDates = async () => {
    try {
      const values = await datesForm.validateFields();
      if (!values.endTime.isAfter(values.startTime)) {
        message.error("End date must be after start date");
        return;
      }
      setSavingDates(true);
      await updateContestDatesApi(editingContest.id, {
        name:           values.name,
        bannerImageUrl: editBannerUrl || null,
        startTime:      values.startTime.format("YYYY-MM-DDTHH:mm:ss"),
        endTime:        values.endTime.format("YYYY-MM-DDTHH:mm:ss"),
      });
      message.success("Contest updated");
      setDatesOpen(false);
      fetchContests();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.data || "Failed to update dates");
    } finally {
      setSavingDates(false);
    }
  };

  const handleEndContest = (contest) => {
    Modal.confirm({
      title: "End this month's contest?",
      content: "The top-scoring entry will be crowned winner, awarded a reward bonus, and featured on the mobile home screen. This cannot be undone.",
      okText: "End Contest & Pick Winner",
      okButtonProps: { style: { background: ACCENT, border: "none" } },
      onOk: async () => {
        try {
          await endContestApi(contest.id);
          message.success("Contest ended — winner crowned and featured on home");
          fetchContests();
        } catch {
          message.error("Failed to end contest");
        }
      },
    });
  };

  const columns = [
    {
      title: "Banner",
      width: 70,
      render: (_, r) => (
        r.bannerUrl ? (
          <Image src={r.bannerUrl} width={48} height={48} style={{ objectFit: "cover", borderRadius: 8 }} />
        ) : (
          <div style={{ width: 48, height: 48, borderRadius: 8, background: "#f5f5f5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>
            📷
          </div>
        )
      ),
    },
    {
      title: "Contest",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600 }}>{r.name || `${MONTH_NAMES[r.month]} ${r.year}`}</div>
          {r.name && <div style={{ fontSize: 11, color: "#9CA3AF" }}>{MONTH_NAMES[r.month]} {r.year}</div>}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => (
        <Tag color={v === "ACTIVE" ? "green" : v === "INACTIVE" ? "orange" : "default"}>{v}</Tag>
      ),
    },
    {
      title: "Start",
      dataIndex: "startTime",
      render: (v) => v ? dayjs(v).format("DD MMM YYYY, h:mm A") : "—",
    },
    {
      title: "End",
      dataIndex: "endTime",
      render: (v) => v ? dayjs(v).format("DD MMM YYYY, h:mm A") : "—",
    },
    {
      title: "Actions",
      width: 70,
      align: "center",
      render: (_, r) => (
        <Dropdown
          trigger={["click"]}
          menu={{
            items: [
              { key: "view", label: "View Entries", icon: <EyeOutlined />, onClick: () => openLeaderboard(r) },
              { key: "dates", label: "Edit Dates", icon: <CalendarOutlined />, onClick: () => openEditDates(r) },
              ...(r.status !== "ENDED" ? [{
                key: "toggle",
                label: r.status === "ACTIVE" ? "Deactivate" : "Activate",
                icon: <PoweroffOutlined />,
                onClick: () => handleToggleContestActive(r),
              }] : []),
              ...(r.status === "ACTIVE" ? [{
                key: "end",
                label: "End & Pick Winner",
                icon: <TrophyOutlined />,
                onClick: () => handleEndContest(r),
              }] : []),
              { type: "divider" },
              { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDeleteContest(r) },
            ],
          }}
        >
          <Button icon={<MoreOutlined />} size="small" />
        </Dropdown>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Monthly Baby Contest</div>
          <div className="page-sub">
            Each calendar month gets its own contest automatically — admin uploads the photo and details for each entry.
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button icon={<CalendarOutlined />} onClick={openCreateContest} style={{ borderRadius: 8 }}>
            Create Contest
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            style={{ background: ACCENT, border: "none", borderRadius: 8 }}
            onClick={openAddEntry}
          >
            Add Entry
          </Button>
        </div>
      </div>

      <AppTable columns={columns} data={contests} pagination={{ pageSize: 8 }} />

      <Modal
        title={activeContest ? `${activeContest.name || `${MONTH_NAMES[activeContest.month]} ${activeContest.year}`} — Entries` : "Entries"}
        open={leaderboardOpen}
        onCancel={() => setLeaderboardOpen(false)}
        footer={null}
        width={640}
      >
        {loadingBoard ? (
          <div style={{ textAlign: "center", padding: 24 }}>Loading…</div>
        ) : leaderboard.length === 0 ? (
          <Empty description="No entries yet" />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {leaderboard.map((e) => (
              <div
                key={e.id}
                style={{
                  display: "flex",
                  gap: 12,
                  alignItems: "center",
                  border: "1px solid #eee",
                  borderRadius: 10,
                  padding: 10,
                  background: e.winner ? "#FFF8E1" : e.active === false ? "#FAFAFA" : "#fff",
                  opacity: e.active === false ? 0.6 : 1,
                }}
              >
                <span style={{ fontWeight: 700, color: "#9CA3AF", width: 24 }}>#{e.rank}</span>
                {e.photoUrl ? (
                  <Image src={e.photoUrl} width={56} height={56} style={{ objectFit: "cover", borderRadius: 8 }} />
                ) : (
                  <div style={{ width: 56, height: 56, borderRadius: 8, background: "#f5f5f5" }} />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>
                    Entry #{e.rank}{" "}
                    {e.winner && <Tag color="gold" icon={<TrophyOutlined />}>{e.badge || "Winner"}</Tag>}
                    {e.active === false && <Tag color="default">Inactive</Tag>}
                  </div>
                  <div style={{ fontSize: 12, color: "#6B7280" }}>{e.caption}</div>
                  <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 2 }}>
                    ❤️ {e.likesCount} · 💬 {e.commentsCount} · Score {e.score}
                  </div>
                </div>
                <Dropdown
                  trigger={["click"]}
                  menu={{
                    items: [
                      { key: "edit", label: "Edit", icon: <EditOutlined />, onClick: () => openEditEntry(e) },
                      {
                        key: "toggle",
                        label: e.active === false ? "Activate" : "Deactivate",
                        icon: <PoweroffOutlined />,
                        onClick: () => handleToggleEntryActive(e),
                      },
                      { type: "divider" },
                      { key: "delete", label: "Delete", icon: <DeleteOutlined />, danger: true, onClick: () => handleDeleteEntry(e) },
                    ],
                  }}
                >
                  <Button icon={<MoreOutlined />} size="small" />
                </Dropdown>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        title={editingEntry ? "Edit Contest Entry" : "Add Contest Entry"}
        open={addOpen}
        onCancel={() => setAddOpen(false)}
        onOk={handleSaveEntry}
        okText={editingEntry ? "Save Changes" : "Add Entry"}
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading: saving }}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item label="Photo" required>
            <Upload
              accept="image/*"
              beforeUpload={handleUpload}
              showUploadList={false}
              disabled={uploading}
            >
              <Button icon={<UploadOutlined />} loading={uploading}>
                {photoUrl ? "Replace Photo" : "Upload Photo"}
              </Button>
            </Upload>
            {photoUrl && (
              <Image src={photoUrl} width={80} height={80} style={{ objectFit: "cover", borderRadius: 8, marginTop: 8 }} />
            )}
          </Form.Item>

          <Form.Item name="caption" label="Caption">
            <Input.TextArea rows={3} placeholder="Write a caption…" maxLength={140} showCount />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingContest ? `Edit — ${editingContest.name || `${MONTH_NAMES[editingContest.month]} ${editingContest.year}`}` : "Edit Contest"}
        open={datesOpen}
        onCancel={() => setDatesOpen(false)}
        onOk={handleSaveDates}
        okText="Save Changes"
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading: savingDates }}
        destroyOnClose
      >
        <Form form={datesForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="name" label="Contest Name" rules={[{ required: true, message: "Give the contest a name" }]}>
            <Input placeholder="e.g. June Baby Stars" maxLength={60} />
          </Form.Item>

          <Form.Item label="Contest Banner">
            <Upload
              accept="image/*"
              beforeUpload={handleEditBannerUpload}
              showUploadList={false}
              disabled={editBannerUploading}
            >
              <Button icon={<UploadOutlined />} loading={editBannerUploading}>
                {editBannerUrl ? "Replace Photo" : "Upload Photo"}
              </Button>
            </Upload>
            {editBannerUrl && (
              <Image src={editBannerUrl} width={80} height={80} style={{ objectFit: "cover", borderRadius: 8, marginTop: 8 }} />
            )}
          </Form.Item>

          <Form.Item name="startTime" label="Start Date" rules={[{ required: true, message: "Pick a start date" }]}>
            <DatePicker showTime format="DD MMM YYYY, h:mm A" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="endTime" label="Expiry Date" rules={[{ required: true, message: "Pick an expiry date" }]}>
            <DatePicker showTime format="DD MMM YYYY, h:mm A" style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Create Contest"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreateContest}
        okText="Create Contest"
        okButtonProps={{ style: { background: ACCENT, border: "none" }, loading: creatingContest }}
        destroyOnClose
      >
        <Form form={createForm} layout="vertical" style={{ marginTop: 12 }}>
          <Form.Item name="name" label="Contest Name" rules={[{ required: true, message: "Give the contest a name" }]}>
            <Input placeholder="e.g. June Baby Stars" maxLength={60} />
          </Form.Item>
          <div style={{ display: "flex", gap: 12 }}>
            <Form.Item name="month" label="Month" rules={[{ required: true, message: "Select a month" }]} style={{ flex: 2 }}>
              <Select
                placeholder="Select month"
                options={MONTH_NAMES.slice(1).map((m, i) => ({ value: i + 1, label: m }))}
              />
            </Form.Item>
            <Form.Item name="year" label="Year" rules={[{ required: true, message: "Enter a year" }]} style={{ flex: 1 }}>
              <InputNumber min={2024} max={2100} style={{ width: "100%" }} />
            </Form.Item>
          </div>
          <Form.Item name="startTime" label="Start Date" rules={[{ required: true, message: "Pick a start date" }]}>
            <DatePicker showTime format="DD MMM YYYY, h:mm A" style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="endTime" label="End Date" rules={[{ required: true, message: "Pick an end date" }]}>
            <DatePicker showTime format="DD MMM YYYY, h:mm A" style={{ width: "100%" }} />
          </Form.Item>

          <Form.Item label="Contest Banner (optional)">
            <Upload
              accept="image/*"
              beforeUpload={handleCreateUpload}
              showUploadList={false}
              disabled={createUploading}
            >
              <Button icon={<UploadOutlined />} loading={createUploading}>
                {createPhotoUrl ? "Replace Photo" : "Upload Photo"}
              </Button>
            </Upload>
            {createPhotoUrl && (
              <Image src={createPhotoUrl} width={80} height={80} style={{ objectFit: "cover", borderRadius: 8, marginTop: 8 }} />
            )}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
